import React, { ReactElement, useEffect, useState } from 'react';
import { AuthState } from '@aws-amplify/ui-components';
import { Auth } from 'aws-amplify';
// styles
import './App.css';

import { postSignInUtilities } from './components/auth/postSignInUtils';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import SuccessfulEmailValidation from './components/emailVerificationRedirection/successfulEmailValidation';
import FailedEmailValidation from './components/emailVerificationRedirection/failedEmailValidation';
import { Button, Layout, Modal, Typography } from 'antd';
import { Redirect } from 'react-router-dom';
import EnvironmentsPage from './components/apiKeys/EnvironmentsPage';
import { Channels } from './components/common/channelsEnum';
import NotificationTabsContainer from './components/notifications/NotificationTabsContainer';
import PageLayout from './components/PageLayout';
import SettingsPage from './components/settings/SettingsPage';
import NewEmailComponents from './components/settings/tabs/emailComponents/newEmailComponents';
import { AppProvider } from './context/context';
import NewNotification from './components/notifications/newNotification';
import Bee from './plugins/BeeEmailEditor';
import { Components } from './components/common/componentsEnum';
import SignOut from './components/auth/signOut';
import { User } from './interfaces/api/Auth';
import PlanContainer from './components/plan/PlanContainer';
import { DynamoDB, config } from 'aws-sdk';
import Logs from './components/logs/Logs';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import Unsubscribe from './components/unsubscribe/Unsubscribe';
import { useIdleTimer } from 'react-idle-timer';
import InsightsContainer from './components/insights/InsightsContainer';
import { readWebsiteVariable } from './utils/readWebsiteVariable';
import OnboardingContainer from './components/onboarding/OnboardingContainer';
import NotificationsContainer from './components/notifications/NotificationsContainer';
import AuthContainer from './components/auth/AuthContainer';
import { setAWSCreds } from './utils/accessToken';
import { Notification } from './interfaces/backend/Notification';
import InAppContainer from './components/InApp/InAppContainer';
import UsersContainer from './components/Users/UsersContainer';

const cognitoRegion = readWebsiteVariable('REACT_APP_aws_cognito_region');

export type AccountSummary = {
  hasNotification: boolean;
  hasInApp: boolean;
  hasUsers: boolean;
};

const App = (): ReactElement => {
  const [authState, setAuthState] = useState(AuthState.SignedIn); // user is assumed signedin when loading
  const [user, setUser] = useState<User>();
  const [envId, setEnvId] = useState<string>();
  const [admin, setAdmin] = useState(false);
  const [idle, setIdle] = useState(false);
  const [accountSummary, setAccountSummary] = useState<AccountSummary>({
    hasNotification: false,
    hasInApp: false,
    hasUsers: false
  });
  const [checking, setChecking] = useState(true);

  useIdleTimer({
    onIdle: () => {
      setIdle(true);
    },
    timeout: 11 * 3600 * 1000
  });

  // Page Load: Restore Authentication State
  useEffect(() => {
    config.region = cognitoRegion;
    // confirm sign in and set tokens, otherwise signout
    if (AuthState.SignedIn)
      Auth.currentUserPoolUser({
        bypassCache: true // this is because the back-end adds envId to the user after signup
      })
        .then((cognitoUser) => {
          cognitoUser.getSession((err: Error, session: CognitoUserSession) => {
            if (err) console.log(err);
            if (session) {
              // set group
              const groups = session.getAccessToken().payload['cognito:groups'];
              setAdmin(groups && groups.includes('superadmin'));
              postSignInUtilities(cognitoUser);
              setUser(cognitoUser);
              setAuthState(AuthState.SignedIn);
            }
          });
        })
        .catch(() => {
          localStorage.clear();
          setUser(undefined);
          setAuthState(AuthState.SignedOut);
        });
  }, [authState]);

  // accountStatus
  useEffect(() => {
    if (!envId) return;
    let interval: NodeJS.Timeout;
    const updateAccountSummary = async () => {
      try {
        let hasNotification, hasInApp, hasUsers;
        await setAWSCreds();
        const ddb = new DynamoDB.DocumentClient();
        const notiItems = await ddb
          .query({
            KeyConditionExpression: 'envId = :envId',
            ExpressionAttributeValues: {
              ':envId': envId
            },
            TableName: 'notificationsTable5'
          })
          .promise();
        if (notiItems.Count && notiItems.Count > 0 && notiItems.Items) {
          hasNotification = true;
          hasInApp = (notiItems.Items as Notification[]).some(
            (n: Notification) => n.channels.includes(Channels.INAPP_WEB)
          );
        } else {
          hasNotification = false;
          hasInApp = false;
        }

        const userItems = await ddb
          .query({
            KeyConditionExpression: 'envId = :envId',
            ExpressionAttributeValues: {
              ':envId': envId
            },
            TableName: 'usersTable1'
          })
          .promise();
        if (userItems.Count && userItems.Count > 0) {
          hasUsers = true;
        } else {
          hasUsers = false;
        }

        setAccountSummary({
          hasNotification,
          hasInApp,
          hasUsers
        });

        if (hasNotification && hasInApp && hasUsers) {
          setChecking(false);
          clearInterval(interval);
        }
      } catch (err) {
        console.log(err);
        setChecking(false);
        clearInterval(interval);
      }
    };

    if (checking) {
      updateAccountSummary();
      interval = setInterval(updateAccountSummary, 3000);
    }
    return () => clearInterval(interval);
  }, [envId, checking]);

  // setting the envId
  useEffect(() => {
    if (admin && localStorage.getItem('adminInEnvId')) {
      setEnvId(localStorage.getItem('adminInEnvId') as string);
    } else if (user?.attributes['custom:envIds']) {
      setEnvId(user.attributes['custom:envIds']);
    }
  }, [user, admin]);

  return (
    <>
      {authState !== AuthState.Loading && (
        // eslint-disable-next-line
        //@ts-ignore
        <Router>
          <Switch>
            {/* Neutral Routes */}
            <Route path="/successfulEmailValidation">
              <SuccessfulEmailValidation />
            </Route>
            <Route path="/failedEmailValidation">
              <FailedEmailValidation />
            </Route>

            {/* Public Only Routes */}
            <Route path="/signup">
              <AuthContainer
                authState={authState}
                setAuthState={setAuthState}
                setUser={setUser}
              />
            </Route>
            <Route path="/signin">
              <AuthContainer
                authState={authState}
                setAuthState={setAuthState}
                setUser={setUser}
              />
            </Route>
            <Route path="/resetpassword">
              <AuthContainer
                authState={authState}
                setAuthState={setAuthState}
                setUser={setUser}
              />
            </Route>
            <Route path="/unsubscribe">
              <Unsubscribe />
            </Route>

            {/* Protectec Only Routes */}
            <Route path="/">
              <Modal
                title="Session Expired"
                closable={false}
                open={idle}
                footer={[
                  <Button
                    type="primary"
                    onClick={() => window.location.reload()}
                    key="refresh-button"
                  >
                    Refresh
                  </Button>
                ]}
              >
                <Typography.Paragraph>
                  Your session has expired. Please refresh the page.
                </Typography.Paragraph>
              </Modal>
              {authState === AuthState.SignedOut && <Redirect to="/signin" />}
              {envId && (
                <AppProvider>
                  <div className="App">
                    <Layout style={{ minHeight: '100vh' }}>
                      <Switch>
                        {user?.attributes['custom:signUpCompleted'] !==
                          'true' && (
                          <Route path="/" exact>
                            <Redirect to={`/${envId}/onboarding`} />
                          </Route>
                        )}

                        <Route path="/" exact>
                          <Redirect to={`/${envId}/notifications`} />
                        </Route>
                        {/* Onboarding */}
                        <Route path="/:accountId/onboarding">
                          <PageLayout
                            sliderItem="Onboarding"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <OnboardingContainer />
                          </PageLayout>
                        </Route>

                        {/* InApp */}
                        <Route path="/:accountId/inapp">
                          <PageLayout
                            sliderItem="inapp"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <InAppContainer />
                          </PageLayout>
                        </Route>

                        {/* Users */}
                        <Route path="/:accountId/users">
                          <PageLayout
                            sliderItem="users"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <UsersContainer />
                          </PageLayout>
                        </Route>

                        {/* Settings */}
                        <Route path="/:accountId/settings/:tabId">
                          <PageLayout
                            sliderItem="Setting"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <SettingsPage />
                          </PageLayout>
                        </Route>
                        <Route path="/:accountId/settings">
                          <PageLayout
                            sliderItem="Settings"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <SettingsPage />
                          </PageLayout>
                        </Route>
                        <Route
                          exact
                          path="/settings"
                          render={(props) => (
                            <Redirect to={`/${envId}/settings`} />
                          )}
                        />
                        <Route
                          exact
                          path="/settings/:tabId"
                          render={(props) => (
                            <Redirect
                              to={`/${envId}/settings/${props.match.params.tabId}`}
                            />
                          )}
                        />
                        <Route path="/:accountId/plan">
                          <PageLayout
                            sliderItem="Plan"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <PlanContainer />
                          </PageLayout>
                        </Route>
                        <Route path="/:accountId/insights">
                          <PageLayout
                            sliderItem="Insights"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <InsightsContainer />
                          </PageLayout>
                        </Route>
                        <Route path="/:accountId/logs">
                          <PageLayout
                            sliderItem="Logs"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <Logs />
                          </PageLayout>
                        </Route>
                        <Route path="/:accountId/environments">
                          <PageLayout
                            sliderItem="Environments"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <EnvironmentsPage />
                          </PageLayout>
                        </Route>
                        <Route
                          path={`/:accountId/beeEditor/:templateId/componentName/:savesRowName`}
                        >
                          <Bee channel={Components.EMAIL} />
                        </Route>
                        <Route
                          path={`/:accountId/notifications/:notificationId/beeEditor/:templateId`}
                        >
                          <Bee channel={Channels.EMAIL} />
                        </Route>
                        <Route exact path={`/:accountId/newnotification`}>
                          <PageLayout
                            sliderItem="Notifications"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <NewNotification />
                          </PageLayout>
                        </Route>
                        <Route exact path={`/:accountId/${Components.EMAIL}`}>
                          <PageLayout
                            sliderItem="Settings"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <NewEmailComponents />
                          </PageLayout>
                        </Route>
                        <Route
                          exact
                          path={`/:accountId/notifications/:notificationId/notificationConfig/:tabId`}
                        >
                          <PageLayout
                            sliderItem="Notifications"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <NotificationTabsContainer
                              accountSummary={accountSummary}
                            />
                          </PageLayout>
                        </Route>
                        <Route path={`/:accountId/notifications`}>
                          <PageLayout
                            sliderItem="Notifications"
                            envId={envId}
                            setEnvId={setEnvId}
                            admin={admin}
                            accountSummary={accountSummary}
                          >
                            <NotificationsContainer />
                          </PageLayout>
                        </Route>
                        <Route path="/signout">
                          <SignOut
                            setAuthState={setAuthState}
                            setUser={setUser}
                            setEnvId={setEnvId}
                          />
                        </Route>

                        {/* TODO: /environments should not be {envId}/environments */}
                        <Route path={`/environments`}>
                          <Redirect
                            exact
                            from="/"
                            to={`${envId}/environments`}
                          />
                        </Route>
                      </Switch>
                    </Layout>
                  </div>
                </AppProvider>
              )}
            </Route>
          </Switch>
        </Router>
      )}
    </>
  );
};

export default App;
